// ==================== REPORTS PAGE ====================
// src/pages/Reports.tsx
import React, { useState, useEffect } from "react";
import { api } from "../api/client";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

type Theme = {
  theme_id: string;
  title: string;
  session_ids: string[];
  count: number;
};

type BlockedAction = {
  id: string;
  text: string;
  ownerName: string;
  blockedDays: number;
  blockReason: string;
};

type RetroTimelinePoint = {
  date: string;
  retros: number;
  items: number;
};

type MonthlyReport = {
  retrosCount: number;
  participantsCount: number;
  actionsCreated: number;
  actionsDone: number;
  actionsOpen: number;
  actionsBlocked: number;
  actionsOverdue: number;
  themes: Theme[];
  retroTimeline: RetroTimelinePoint[];
  blockedActions: BlockedAction[];
};

export default function Reports() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReport();
  }, [month]);

  async function loadReport() {
    try {
      const data = await api.getMonthlyReport(month);
      setReport(data);
    } catch (error) {
      console.error("Failed to load report", error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Monthly Report</h1>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <MetricCard title="Retros Run" value={report?.retrosCount || 0} />
        <MetricCard
          title="Participants"
          value={report?.participantsCount || 0}
        />
        <MetricCard
          title="Actions Created"
          value={report?.actionsCreated || 0}
        />
        <MetricCard
          title="Completion Rate"
          value={`${Math.round(((report?.actionsDone || 0) / Math.max(report?.actionsCreated || 1, 1)) * 100)}%`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Top Recurring Themes">
          {(report?.themes?.length || 0) > 0 ? (
            <div className="space-y-3">
              {(report?.themes || []).slice(0, 5).map((theme: Theme, i: number) => (
                <div
                  key={theme.theme_id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div>
                    <div className="font-medium">{theme.title}</div>
                    <div className="text-sm text-gray-600">
                      Appeared in {theme.session_ids.length} retros
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600">
                    {theme.count}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">No themes data</div>
          )}
        </Card>

        <Card title="Action Items Status">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                { name: "Done", value: report?.actionsDone || 0 },
                { name: "Open", value: report?.actionsOpen || 0 },
                { name: "Blocked", value: report?.actionsBlocked || 0 },
                { name: "Overdue", value: report?.actionsOverdue || 0 },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#4F46E5" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Retros Over Time">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={report?.retroTimeline || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="retros"
              stroke="#4F46E5"
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="items"
              stroke="#10B981"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Currently Blocked Action Items">
        {(report?.blockedActions?.length || 0) > 0 ? (
          <div className="space-y-3">
            {(report?.blockedActions || []).map((action: BlockedAction) => (
              <div
                key={action.id}
                className="p-4 bg-red-50 border border-red-200 rounded"
              >
                <div className="font-medium mb-1">{action.text}</div>
                <div className="text-sm text-gray-600 mb-2">
                  Owner: {action.ownerName} • Blocked for {action.blockedDays}{" "}
                  days
                </div>
                <div className="text-sm text-red-700">
                  Reason: {action.blockReason}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-500 text-center py-8">
            No blocked actions
          </div>
        )}
      </Card>
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: number | string }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="text-sm font-medium text-gray-600 mb-2">{title}</div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}
